import {
  Box,
  MenuItem,
  TextField,
  Typography
} from '@mui/material';

import { PaintBrush } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import {
  INode,
  ITypeNode,
  useFormBuilder,
} from '../../Context/FormBuilderContext';
import { ActionTypes } from '../../Reducer';
import { SubmitFormData } from '../../schemas/submit.schema';
import {
  submitFormToNode,
  submitNodeToForm,
} from '../../utils/nodeDataTransformers';
import { CustomizationForm } from '../FormCustomization/CustomizationForm';

interface SubmitSettingsProps {
  node: INode;
}

export function SubmitSettings({ node }: SubmitSettingsProps) {
  const { dispatch, state } = useFormBuilder();
  const [activeTab, setActiveTab] = useState(0);

  const formsMethods = useForm<SubmitFormData>({
    defaultValues: submitNodeToForm(node),
  });

  useEffect(() => {
    formsMethods.reset(submitNodeToForm(node));
  }, [node.id, node.data, formsMethods]);

  useEffect(() => {
    const subscription = formsMethods.watch(data => {
      if (data && Object.keys(data).length > 0) {
        const updatedData = submitFormToNode(data as SubmitFormData, node);
        dispatch({
          type: ActionTypes.UPDATE_NODES_DATA,
          payload: {
            id: node.id,
            updates: { data: updatedData },
          },
        });
      }
    });

    return () => subscription.unsubscribe();
  }, [formsMethods.watch, node.id, dispatch]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '16px 16px 0 16px',
        }}
      >
        <PaintBrush size={16} color="#101828" />
        <Typography
          style={{ fontSize: '14px', fontWeight: '500', color: '#101828' }}
        >
          Submit Settings
        </Typography>
      </Box>

      <FormProvider {...formsMethods}>
        <form style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Box
            sx={{
              display: 'flex',
              gap: '4px',
              padding: '0px 16px 0 16px',
            }}
          >
            <Box
              onClick={() => setActiveTab(0)}
              sx={{
                px: 2,
                py: 1,
                cursor: 'pointer',
                color: activeTab === 0 ? '#4F46E5' : '#6B7280',
                backgroundColor: activeTab === 0 ? '#F3F4F6' : 'transparent',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: activeTab === 0 ? '#F3F4F6' : '#F9FAFB',
                },
              }}
            >
              Content
            </Box>

            <Box
              onClick={() => setActiveTab(1)}
              sx={{
                px: 2,
                py: 1,
                cursor: 'pointer',
                color: activeTab === 1 ? '#4F46E5' : '#6B7280',
                backgroundColor: activeTab === 1 ? '#F3F4F6' : 'transparent',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: activeTab === 1 ? '#F3F4F6' : '#F9FAFB',
                },
              }}
            >
              Design
            </Box>
          </Box>

          <div
            style={{ width: '100%', height: '1px', backgroundColor: '#EAECF0' }}
          />

          {activeTab === 0 && (
            <Box
              sx={{
                padding: '0px 16px 0 16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
              }}
            >
              {/* <FormControl component="fieldset" style={{ width: '100%' }}>
                <Controller
                  name="removeOrRetain"
                  control={formsMethods.control}
                  render={({ field }) => (
                    <RadioGroup
                      value={field.value ? 'true' : 'false'}
                      onChange={e => field.onChange(e.target.value === 'true')}
                    >
                      <FormControlLabel
                        value="true"
                        control={<Radio size="small" />}
                        label="Remove uploaded files"
                        style={{ fontSize: '10px' }}
                      />
                      <FormControlLabel
                        value="false"
                        control={<Radio size="small" />}
                        label="Keep uploaded files"
                        style={{ fontSize: '10px' }}
                      />
                    </RadioGroup>
                  )}
                />
              </FormControl> */}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Typography
                  style={{
                    fontSize: '12px',
                    fontWeight: '400',
                    color: '#475467',
                    fontStyle: 'normal',
                  }}
                >
                  Redirect to page after submit
                </Typography>
                <Controller
                  name="redirectPageId"
                  control={formsMethods.control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      sx={{
                        width: '100%',
                        '& .MuiInputBase-root': {
                          height: '32px',
                          borderRadius: '4px',
                          border: '1px solid #EAECF0',
                          backgroundColor: '#F9FAFB',
                          color: '#475467',
                          margin: '0px',
                        },
                      }}
                      variant="outlined"
                      size="small"
                    >
                      <MenuItem value="">
                        <em style={{ color: '#98A2B3' }}>
                          None (stay on current page)
                        </em>
                      </MenuItem>
                      {state.form.pages?.map(page => (
                        <MenuItem key={page.id} value={page.id}>
                          {page.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </div>
            </Box>
          )}

          {activeTab === 1 && (
            <Box
              sx={{
                padding: '0px 16px 0 16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
              }}
            >
              <CustomizationForm
                control={formsMethods.control}
                type={ITypeNode.SUBMIT}
              />
            </Box>
          )}
        </form>
      </FormProvider>
    </>
  );
}
